import { useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import ReplayIcon from '@mui/icons-material/Replay';
import BoltIcon from '@mui/icons-material/Bolt';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import type { RootState } from '../../../store';
import {
  useAdminListarPedidosQuery,
  useAdminReenviarPedidoMutation,
  useAdminForcarIntegracaoMutation,
  useAdminMeQuery,
  type PedidoAdminItem,
} from '../../../api';
import { HeaderTela } from '../../../components/admin/header-tela';
import { IconeOrigemPedido, ROTULO_ORIGEM_PEDIDO } from '../../../components/admin/icone-modo';
import { DialogConfirmar } from '../../../components/admin/dialog-confirmar';
import { SnackbarFeedback, useFeedback } from '../../../components/admin/feedback';
import { formatarBRL } from '../../../lib/formato';
import { intervaloDoPeriodo } from './periodo';
import {
  FiltrosPedidos,
  FILTROS_PEDIDOS_VAZIOS,
  temFiltroAtivo,
  type EstadoFiltrosPedidos,
} from './filtros-pedidos';

/**
 * Rótulo + cores de cada situação. As cores de fundo espelham o padrão do PDV
 * Sischef (sischef-app → `util/board/BoardUtil.js` → `SITUACOES`) para
 * consistência visual entre os dois sistemas. As situações que só existem no
 * cardápio (AGUARDANDO_PAGAMENTO, FALHA_ENVIO) recebem cores
 * próprias, coerentes com a paleta. `corTexto` é escolhida por situação para
 * legibilidade: texto escuro em fundos claros/médios (amarelo, verde, ciano,
 * cinza), branco nos escuros (roxo, marrom, vermelhos).
 */
const TEXTO_ESCURO = 'rgba(0, 0, 0, 0.87)';
const COR_SITUACAO_DESCONHECIDA = '#9e9e9e';
const ROTULOS_SITUACAO: Record<string, { label: string; cor: string; corTexto: string }> = {
  PENDENTE: { label: 'Pendente', cor: '#ffc107', corTexto: TEXTO_ESCURO },
  AGUARDANDO_PAGAMENTO: { label: 'Aguardando pagamento', cor: '#ff9800', corTexto: TEXTO_ESCURO },
  CONFIRMADO: { label: 'Confirmado', cor: '#4caf50', corTexto: '#fff' },
  AGUARDANDO_RETIRADA: { label: 'Aguardando retirada', cor: '#22b3dd', corTexto: TEXTO_ESCURO },
  ENVIADO: { label: 'Enviado', cor: '#795548', corTexto: '#fff' },
  ENTREGUE: { label: 'Entregue', cor: '#bdbdbd', corTexto: TEXTO_ESCURO },
  CANCELADO: { label: 'Cancelado', cor: '#ef5350', corTexto: '#fff' },
  FALHA_ENVIO: { label: 'Falha no envio', cor: '#d32f2f', corTexto: '#fff' },
};

const fmtData = new Intl.DateTimeFormat('pt-BR', { dateStyle: 'short', timeStyle: 'short' });

export function AdminPedidosPage(): JSX.Element {
  const idConfig = useSelector((s: RootState) => s.adminAuth.idConfiguracaoSelecionada)!;
  const [pagina, setPagina] = useState(0); // 0-based (MUI)
  const [tamanhoPagina, setTamanhoPagina] = useState(25);
  const [filtros, setFiltros] = useState<EstadoFiltrosPedidos>(FILTROS_PEDIDOS_VAZIOS);

  // Estável ao longo do dia (o preset sempre cai na virada), então não refaz a
  // consulta a cada re-render.
  const intervalo = useMemo(
    () => intervaloDoPeriodo(filtros.periodo, { de: filtros.de, ate: filtros.ate }),
    [filtros.periodo, filtros.de, filtros.ate],
  );

  const { data, isLoading, isFetching, refetch } = useAdminListarPedidosQuery({
    idConfig,
    pagina: pagina + 1,
    tamanhoPagina,
    origem: filtros.origem || undefined,
    de: intervalo.de,
    ate: intervalo.ate,
    busca: filtros.busca || undefined,
  });
  // Mantém a última resposta visível enquanto a próxima página carrega, pra
  // não esvaziar a tabela/paginação durante o fetch.
  const ultimaResposta = useRef<{ itens: PedidoAdminItem[]; total: number }>({ itens: [], total: 0 });
  if (data) ultimaResposta.current = { itens: data.itens, total: data.total };
  const pedidos = data?.itens ?? ultimaResposta.current.itens;
  const total = data?.total ?? ultimaResposta.current.total;
  const { data: adminMe } = useAdminMeQuery();
  const [reenviar] = useAdminReenviarPedidoMutation();
  const [forcar] = useAdminForcarIntegracaoMutation();
  const [reenviandoId, setReenviandoId] = useState<string | null>(null);
  const [forcandoId, setForcandoId] = useState<string | null>(null);
  const { feedback, mostrarSucesso, mostrarErro, fecharFeedback } = useFeedback();
  /** Pedido aguardando confirmação de "forçar integração" — `null` = nenhum. */
  const [forcandoConfirmar, setForcandoConfirmar] = useState<string | null>(null);

  async function handleReenviar(idPedido: string): Promise<void> {
    setReenviandoId(idPedido);
    try {
      await reenviar(idPedido).unwrap();
      mostrarSucesso(`Pedido #${idPedido.slice(0, 8)} reenviado ao Sischef com sucesso`);
    } catch (err) {
      mostrarErro(extrairMsg(err) ?? 'Não foi possível reenviar o pedido ao Sischef');
    } finally {
      setReenviandoId(null);
    }
  }

  async function handleForcar(idPedido: string): Promise<void> {
    setForcandoId(idPedido);
    try {
      await forcar(idPedido).unwrap();
      mostrarSucesso(`Integração do pedido #${idPedido.slice(0, 8)} forçada com sucesso`);
    } catch (err) {
      mostrarErro(extrairMsg(err) ?? 'Não foi possível forçar a integração');
    } finally {
      setForcandoId(null);
    }
  }

  function aplicarFiltros(novo: EstadoFiltrosPedidos): void {
    setFiltros(novo);
    setPagina(0);
  }

  return (
    <>
      <HeaderTela
        titulo="Pedidos"
        descricao="Pedidos recebidos pelo cardápio e o resultado da integração com o Sischef."
        acoes={
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => refetch()}
            disabled={isFetching}
            fullWidth
            sx={{ width: { xs: '100%', sm: 'auto' } }}
          >
            Atualizar
          </Button>
        }
      />

      <FiltrosPedidos valor={filtros} aoMudar={aplicarFiltros} />

      <Paper variant="outlined" sx={{ overflowX: 'auto' }}>
        {isLoading ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <CircularProgress />
          </Box>
        ) : (
          <Table size="small" sx={{ minWidth: 760 }}>
            <TableHead>
              <TableRow>
                <TableCell>Origem</TableCell>
                <TableCell>Data</TableCell>
                <TableCell>Pedido</TableCell>
                <TableCell>Sischef</TableCell>
                <TableCell>Cliente</TableCell>
                <TableCell>Pagamento</TableCell>
                <TableCell align="right">Valor</TableCell>
                <TableCell>Situação</TableCell>
                <TableCell>Integração</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {pedidos.map((pedido) => {
                const rotulo = ROTULOS_SITUACAO[pedido.situacao] ?? {
                  label: pedido.situacao,
                  cor: COR_SITUACAO_DESCONHECIDA,
                  corTexto: TEXTO_ESCURO,
                };
                return (
                  <TableRow key={pedido.id} hover>
                    <TableCell>
                      <Chip
                        size="small"
                        variant="outlined"
                        icon={<IconeOrigemPedido origem={pedido.origem} />}
                        label={ROTULO_ORIGEM_PEDIDO[pedido.origem] ?? pedido.origem}
                      />
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {fmtData.format(new Date(pedido.dataCriacao))}
                    </TableCell>
                    <TableCell>
                      <Tooltip title={pedido.id}>
                        <code style={{ fontSize: 12 }}>#{pedido.shortId ?? pedido.id.slice(0, 8)}</code>
                      </Tooltip>
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      <ReferenciaSischef pedido={pedido} />
                    </TableCell>
                    <TableCell>{pedido.clienteNome?.trim() || '—'}</TableCell>
                    <TableCell>{pedido.formaPagamento ?? '—'}</TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                      {formatarBRL(pedido.valorTotal)}
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={rotulo.label}
                        sx={{ bgcolor: rotulo.cor, color: rotulo.corTexto, fontWeight: 500 }}
                      />
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <ChipIntegracao pedido={pedido} />
                        {pedido.situacao === 'FALHA_ENVIO' && (
                          <Tooltip title="Reenviar ao Sischef">
                            <span>
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleReenviar(pedido.id)}
                                disabled={reenviandoId !== null}
                              >
                                {reenviandoId === pedido.id ? (
                                  <CircularProgress size={18} color="inherit" />
                                ) : (
                                  <ReplayIcon fontSize="small" />
                                )}
                              </IconButton>
                            </span>
                          </Tooltip>
                        )}
                        {adminMe?.roleAdmin && (
                          <Tooltip title="Forçar nova integração (ROLE_ADMIN)">
                            <span>
                              <IconButton
                                size="small"
                                color="warning"
                                onClick={() => setForcandoConfirmar(pedido.id)}
                                disabled={forcandoId !== null}
                              >
                                {forcandoId === pedido.id ? (
                                  <CircularProgress size={18} color="inherit" />
                                ) : (
                                  <BoltIcon fontSize="small" />
                                )}
                              </IconButton>
                            </span>
                          </Tooltip>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })}
              {pedidos.length === 0 && (
                <TableRow>
                  <TableCell colSpan={9} align="center">
                    <Typography color="text.secondary" sx={{ py: 4 }}>
                      {temFiltroAtivo(filtros)
                        ? 'Nenhum pedido encontrado com esses filtros.'
                        : 'Nenhum pedido recebido ainda.'}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        )}
        {total > 0 && (
          <TablePagination
            component="div"
            count={total}
            page={pagina}
            onPageChange={(_, novaPagina) => setPagina(novaPagina)}
            rowsPerPage={tamanhoPagina}
            onRowsPerPageChange={(e) => {
              setTamanhoPagina(parseInt(e.target.value, 10));
              setPagina(0);
            }}
            rowsPerPageOptions={[10, 25, 50, 100]}
            labelRowsPerPage="Por página:"
            labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
          />
        )}
      </Paper>

      <DialogConfirmar
        aberto={forcandoConfirmar !== null}
        titulo="Forçar nova integração deste pedido?"
        descricao="O pedido é enviado ao Sischef mesmo se já tiver sido integrado. Pode gerar um pedido duplicado lá."
        textoConfirmar="Forçar integração"
        destrutivo={false}
        aoCancelar={() => setForcandoConfirmar(null)}
        aoConfirmar={() => {
          const id = forcandoConfirmar;
          setForcandoConfirmar(null);
          if (id) void handleForcar(id);
        }}
      />

      <SnackbarFeedback feedback={feedback} aoFechar={fecharFeedback} />
    </>
  );
}

function extrairMsg(err: unknown): string | null {
  if (typeof err === 'object' && err && 'data' in err) {
    const d = (err as { data?: { mensagem?: string } }).data;
    return d?.mensagem ?? null;
  }
  return null;
}

/**
 * Referência do pedido dentro do Sischef: o SEQ (sequencial — o número que o
 * gestor vê nas telas do Sischef) e, no tooltip, o id do pedido_integracao.
 * O vínculo chega assíncrono (webhook após o Sischef processar a fila), então
 * pode ficar "—" por alguns instantes logo após o envio.
 */
function ReferenciaSischef({ pedido }: { pedido: PedidoAdminItem }): JSX.Element {
  if (pedido.idPedidoIntegracao == null && pedido.sequencial == null) {
    return <Typography color="text.secondary">—</Typography>;
  }
  return (
    <Tooltip title={`Pedido Sischef #${pedido.idPedidoIntegracao ?? '?'}`}>
      <code style={{ fontSize: 12 }}>
        {pedido.sequencial != null ? `SEQ ${pedido.sequencial}` : `#${pedido.idPedidoIntegracao}`}
      </code>
    </Tooltip>
  );
}

/**
 * Resultado da integração com o Sischef, derivado da situação:
 * PENDENTE = ainda não enviado; FALHA_ENVIO = erro (tooltip mostra o motivo);
 * qualquer outra situação significa que o Sischef aceitou o pedido.
 */
function ChipIntegracao({ pedido }: { pedido: PedidoAdminItem }): JSX.Element {
  if (pedido.situacao === 'FALHA_ENVIO') {
    return (
      <Tooltip
        title={`${pedido.ultimoErroEnvio ?? 'Erro não registrado'} (${pedido.tentativasEnvio} tentativa${
          pedido.tentativasEnvio === 1 ? '' : 's'
        })`}
      >
        <Chip size="small" color="error" icon={<ErrorOutlineIcon />} label="Falha" />
      </Tooltip>
    );
  }
  if (pedido.situacao === 'PENDENTE') {
    return <Chip size="small" icon={<HourglassEmptyIcon />} label="Pendente" />;
  }
  return <Chip size="small" color="success" variant="outlined" icon={<CheckCircleOutlineIcon />} label="Integrado" />;
}
